import microuniversoApi from "app/api/microuniversoApi";
import { useAppSelector } from "app/redux/hooks";
import { Documento, RespostaCredito, SenhaModal } from "app/types/cliente/RespostaCredito";
import { FiltrosListarTemplateSeguranca } from "app/types/modalImprimir/RespostaModalImprimir";
import { Parametro } from "app/types/Parametro";
import { tratamentoErroPadrao } from "app/use-cases";
import { obterPermissao } from "app/use-cases/obterPermissao";
import { obterParametros, obterParametrosPorOidItem } from "app/use-cases/parametro/obterParametros";
import { Dict } from "app/utils/Dict";
import globalMU from "globalMU";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb2Container } from "../breadcrumb2/Breadcrumb2Container";
import { ModalImprimirContainer } from "../itens-orcamento/orcamento-concluido/modal-imprimir/modalImprimirContainer";
import { Autorizar } from "../modal-permissao-senha/ModalPermissaoSenha";
import { ModalPermissaoSenhaContainer } from "../modal-permissao-senha/ModalPermissaoSenhaContainer";
import { CreditosCliente } from "./CreditosCliente";

export type CreditosClienteContainerProps = {
    codigoCliente: number
    onVoltar: () => void;
    passos: Dict<string, () => void>;
    onRemover: () => boolean
}

const parametroFonteReceita = "UTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA";

export const CreditosClienteContainer = (props: CreditosClienteContainerProps) => {
    const { codigoCliente, onRemover, onVoltar: onVoltarProp, passos } = props;
    const orcamento = useAppSelector((store) => store.orcamento);
    const usuarioLogado = globalMU.sessionStorage.getData('UsuarioLogado');

    const [dadosCreditos, setDadosCreditos] = useState<RespostaCredito>();
    const [parametros, setParametros] = useState<Parametro[]>([]);
    const [usuarioPermitidoImpressao, setUsuarioPermitidoImpressao] = useState<boolean>(false);
    const [dadosPermissaoSenhaVenda, setDadosPermissaoSenhaVenda] = useState<SenhaModal>();
    const [mostrarModalSenha, setMostrarModalSenha] = useState<boolean>(false);
    const [creditosSelecionados, setCreditosSelecionados] = useState<Documento[]>();
    const [mostrarModalImprimir, setMostrarModalImprimir] = useState<boolean>(false);
    const [filtrosTemplates, setFiltrosTemplates] = useState<FiltrosListarTemplateSeguranca>();
    const [colunaOrdenacao, setColunaOrdenacao] = useState<string>("")
    const [ordemOrdenacaoDecrescente, setOrdemOrdenacaoDecrescente] = useState<boolean>(false)
    const [carregando, setCarregando] = useState<boolean>(true);
    const [paramUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA, setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA] = useState<boolean>(false)

    // Toda chamada assíncrona daqui pode terminar depois da tela ser desmontada (o usuário volta no
    // breadcrumb antes da API responder). Sem esta guarda o setState pós-await vaza memória e gera o
    // warning de update em componente desmontado — ver #React-1 (Aprendizado_React.md).
    const isMounted = useRef(true)
    const passosInterno = useRef<Dict<string, () => void>>(new Dict(Object.assign({}, passos.map)))

    const onVoltar = useCallback(() => {
        onRemover()
        onVoltarProp()
    }, [onRemover, onVoltarProp])

    const atualizarParametros = useCallback(async () => {
        const parametrosTela = await obterParametros({
            nomes: [
                "VALIDAR_DESCONTO_MAXIMO_FORMAPGTO_CREDITO_CLIENTE_ORCAMENTO",
                "TRABALHA_CONTROLE_IMPRESSAO_NOTA_CREDITO",
            ],
        });

        if (!isMounted.current) return;
        setParametros(parametrosTela);

        let oidFilial = orcamento?.OidFilialOrcamento ?? 7;

        try {
            if (oidFilial > 7) {
                const param = await obterParametrosPorOidItem(
                    {
                        nomes:
                            [
                                parametroFonteReceita,
                            ], oidItem: oidFilial
                    })

                if (!isMounted.current) return;
                setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA(param.find((p) => p.Nome == parametroFonteReceita)?.Valor == "S") // 487678/449093
            }
            else {
                // se chamado fora do orçamento
                const param = await obterParametros({
                    nomes: [
                        parametroFonteReceita,
                    ],
                })

                if (!isMounted.current) return;
                setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA(param.find((p) => p.Nome == parametroFonteReceita)?.Valor == "S")  // 487678/449093
            }

        }
        catch (e) {
            if (isMounted.current) tratamentoErroPadrao(e)
        }
    }, [orcamento?.OidFilialOrcamento]);

    const valorParametro = useCallback((nome: string) =>
        parametros.find((p) => p.Nome == nome)?.Valor, [parametros]);

    const buscarUsuarioPermitdo = useCallback(async () => {
        const permitido = await obterPermissao({
            funcionalidade: "ORCAMENTO_CLIENTE",
            acao: "IMPRIMIR_CREDITO_CLIENTE"
        });

        if (!isMounted.current) return;
        setUsuarioPermitidoImpressao(permitido);
    }, []);

    const buscarDadosPermissaoSenhaVenda = useCallback(async (documentosSelecionados: Documento[]) => {
        try {
            globalMU.blockUI();
            const permissoesVendedor = [];
            for (let i = 0; i < documentosSelecionados.length; i++) {
                if (documentosSelecionados[i]?.NumeroVias > 0) {
                    permissoesVendedor.push("TIPO_LIBERACAO_LIBERA_REIMPRESSAO_NOTA_CREDITO");
                }
                if (documentosSelecionados[i]?.VencidoParaImpressao) {
                    permissoesVendedor.push("TIPO_LIBERACAO_LIBERA_IMPRESSAO_NOTA_CREDITO_VENCIDA");
                }
            }
            if (permissoesVendedor.length > 0) {
                const senhaVenda = await microuniversoApi.Comum.PermissaoSenhaVenda(
                    permissoesVendedor
                );

                if (!isMounted.current) return;
                setDadosPermissaoSenhaVenda(senhaVenda)
            }
            else {
                if (!isMounted.current) return;
                setMostrarModalImprimir(true) // 450413
                setMostrarModalSenha(false); // 450413
            }
        } catch (erro) {
            if (isMounted.current) tratamentoErroPadrao(erro);
        } finally {
            globalMU.unblockUI();
        }

    }, []);

    const exibirModalSenha = useCallback(async (documentosSelecionados: Documento[]) => {
        if (documentosSelecionados?.length > 0) {
            await buscarDadosPermissaoSenhaVenda(documentosSelecionados);

            if (!isMounted.current) return;
            setMostrarModalSenha(true);
            setCreditosSelecionados(documentosSelecionados);
        }
        else {
            globalMU.showErrorMessage("Por favor, selecione os créditos a serem impressos.");
        }
    }, [buscarDadosPermissaoSenhaVenda]);

    const buscarDadosCreditos = useCallback(async () => {
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Cliente.Credito(
                {
                    TamanhoPagina: 1,
                    NumeroPagina: 20,
                    CodigoCliente: codigoCliente,
                }
            );

            if (!isMounted.current) return;
            !("HasErrors" in resposta) ? setDadosCreditos(resposta) : (setDadosCreditos({}), onVoltar())
        }
        catch (erro) {
            if (isMounted.current) tratamentoErroPadrao(erro);
        }
        finally {
            globalMU.unblockUI();
        }
    }, [codigoCliente, onVoltar]);

    const listarCreditosCliente = useCallback(async (coluna?: string, decrescente?: boolean) => {
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Cliente.listarCreditosCliente(
                {
                    TamanhoPagina: 1,
                    NumeroPagina: 20,
                    CodigoCliente: codigoCliente,
                },
                coluna,
                decrescente
            );

            if (!isMounted.current) return;
            setColunaOrdenacao(coluna ?? "")
            setOrdemOrdenacaoDecrescente(decrescente ?? false)
            setDadosCreditos(resposta)
        }
        catch (erro) {
            if (isMounted.current) tratamentoErroPadrao(erro);
        }
        finally {
            globalMU.unblockUI();
        }
    }, [codigoCliente]);

    const onCloseModalImprimir = useCallback(async () => {
        try {
            globalMU.blockUI();
            setMostrarModalImprimir(false)
            buscarDadosCreditos()
        } catch (erro) {
            if (isMounted.current) tratamentoErroPadrao(erro);
        }
        finally {
            if (isMounted.current) setCreditosSelecionados([]) // 475988
            globalMU.unblockUI();
        }
    }, [buscarDadosCreditos]);

    const validarPermissaoSenhaVenda = useCallback(async (payload: Autorizar) => {
        try {
            globalMU.blockUI();
            const resposta: string = await microuniversoApi.Comum.ValidarPermissaoSenhaVenda(
                payload.codigoSenha,
                payload.permissoes,
            );

            if (!isMounted.current) return;
            if (resposta === "OK") {
                setMostrarModalSenha(false);
                setMostrarModalImprimir(true);
            }
        }
        catch (erro) {
            if (isMounted.current) tratamentoErroPadrao(erro);
        } finally {
            globalMU.unblockUI();
        }
    }, []);

    const buscarDadosFiltrosTemplate = useCallback(async () => {
        try {
            globalMU.blockUI();
            const oidUsuarioLogado = await microuniversoApi.Usuario.obterOid();

            if (!isMounted.current) return;
            setFiltrosTemplates(
                {
                    token: encodeURIComponent(globalMU.token()),
                    codigoUsuario: oidUsuarioLogado,
                    siglaFuncionalidade: "ORCAMENTO_CLIENTE",
                    siglaAcao: "CREDITOS_CLIENTE",
                    codigoFilial: undefined,
                }
            );
        }
        catch (erro) {
            if (isMounted.current) tratamentoErroPadrao(erro);
        } finally {
            globalMU.unblockUI();
        }
    }, []);

    const onPassoSelecionado = useCallback((passo: string) => {
        onRemover()
        passosInterno.current.get(passo)()
    }, [onRemover]);

    const parametrosTemplateImpressao = useMemo(() => [
        {
            nome: "creditos",
            valor: creditosSelecionados?.map((credito) => credito.Codigo.toString())
        },
    ], [creditosSelecionados]);

    const permissoesImpressao = useMemo(() => ({
        //a tela Creditos nao possui capacidadade de mandar email ou imprimir credito(s) em word
        permitidoEnviarEmail: false,
        permitidoImprimirWord: false,
        permitidoAlterarCopiaEmail: false,
        permitidoAlterarCopiaOcultaEmail: false,
        permitidoAlterarAssuntoEmail: false,
        permitidoAlterarMensagemEmail: false,
        permitidoAnexarEmail: false
    }), []);

    useEffect(() => {
        isMounted.current = true

        return () => {
            isMounted.current = false
        }
    }, [])

    // Carga inicial: roda só na montagem de propósito. Colocar as funções nas deps refaria as
    // chamadas de API a cada render em que o pai recriasse os callbacks/o Dict de passos.
    useEffect(() => {
        setCarregando(true);
        passosInterno.current.set("Créditos", () => { })
        atualizarParametros();
        buscarUsuarioPermitdo();
        buscarDadosCreditos();
        buscarDadosFiltrosTemplate();
    }, [])

    useEffect(() => {
        if (carregando && dadosCreditos && parametros.length > 0) {
            setCarregando(false);
        }
    }, [carregando, dadosCreditos, parametros])

    return (carregando
        ? <></>
        :
        <>
            {mostrarModalSenha && dadosPermissaoSenhaVenda &&  // 475988
                <ModalPermissaoSenhaContainer
                    senhaModal={dadosPermissaoSenhaVenda}
                    onAutorizar={validarPermissaoSenhaVenda}
                    onClose={() => setMostrarModalSenha(false)}
                    exibirModal={mostrarModalSenha}
                />
            }

            {mostrarModalImprimir && filtrosTemplates && // 475988
                <ModalImprimirContainer
                    urlBase="/Relatorio/CreditoCliente"
                    parametrosTemplate={parametrosTemplateImpressao}
                    filtrosTemplates={filtrosTemplates}
                    exibirModalImprimir={mostrarModalImprimir}
                    permissaoSenhaModalImprimir={undefined} // 450413
                    onClose={onCloseModalImprimir}
                    permissoes={permissoesImpressao}
                />
            }

            <Breadcrumb2Container
                passos={passosInterno.current.keysToList()}
                id="CreditosClienteContainer"
                exibirBotaoLimpar={false}
                exibirBotaoVoltar={true}
                onPassoSelecionado={onPassoSelecionado}
                onBotaoVoltarClicado={onVoltar}
            />

            <CreditosCliente
                permitirImpressaoCredito={true}// 475988 {valorParametro("TRABALHA_CONTROLE_IMPRESSAO_NOTA_CREDITO") == "S"}
                permitirExibirCamposFormaPagamento={valorParametro("VALIDAR_DESCONTO_MAXIMO_FORMAPGTO_CREDITO_CLIENTE_ORCAMENTO") == "S"} // 450472
                usuarioPermitidoImpressao={usuarioPermitidoImpressao}
                onImprimir={exibirModalSenha}
                cpfcnpj={orcamento.Cliente.CpfCnpj}
                nome={orcamento.Cliente.Nome}
                dadosCredito={dadosCreditos}
                corSelecionado={usuarioLogado.CorFundo}
                textoSelecionado={usuarioLogado.CorTexto}
                onAlterarOrdenacaoDados={listarCreditosCliente}
                colunaOrdenacao={colunaOrdenacao}
                ordemOrdenacaoDecrescente={ordemOrdenacaoDecrescente}
                usaFonteDeReceita={paramUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA} // 487678/449093
            />

        </>
    );
}
